import { useCallback, useEffect, useRef, useState } from 'react'
import { Box, Flex, Text, Spinner } from '@chakra-ui/react'
import { LuRefreshCw, LuShieldAlert, LuFilter } from 'react-icons/lu'
import { InfoWindow, useMap } from '@vis.gl/react-google-maps'

import { restrictedZonesIntelService } from '../../../api/services/restrictedZonesIntel.service'
import {
  type RestrictedZoneFeature,
  type RestrictedZoneFeatureCollection,
  type RestrictedZoneSyncStatus,
  intelRestrictionClassColor,
} from '../../../api/models/response/restrictedZoneIntel'
import { KMap } from '../../../lib/googleMaps'
import SyncStatusPanel from './SyncStatusPanel'
import ZoneDetailModal from './ZoneDetailModal'

// Hex paths from Chakra colorPalette tokens — pathOptions needs raw hex.
const COLOR_HEX: Record<string, string> = {
  red: '#b23a26',
  orange: '#ea580c',
  yellow: '#a8740e',
  amber: '#a8740e',
  gray: '#857b71',
}

interface BboxState {
  minLng: number
  minLat: number
  maxLng: number
  maxLat: number
  zoom: number
}

type HoverState = { position: google.maps.LatLngLiteral; text: string } | null

// Capa GeoJSON imperativa sobre google.maps.Data: renderiza polígonos,
// multipolígonos y puntos (SIGDA) nativamente, con estilo por feature,
// click → modal y hover → tooltip. Reemplaza al <GeoJSON> de react-leaflet.
function ZonesDataLayer({
  features,
  onSelect,
  onHover,
}: {
  features: RestrictedZoneFeature[]
  onSelect: (id: string) => void
  onHover: (h: HoverState) => void
}) {
  const map = useMap()
  const dataRef = useRef<google.maps.Data | null>(null)
  const onSelectRef = useRef(onSelect)
  const onHoverRef = useRef(onHover)
  useEffect(() => { onSelectRef.current = onSelect }, [onSelect])
  useEffect(() => { onHoverRef.current = onHover }, [onHover])

  // Crea la capa una vez y engancha estilo + listeners.
  useEffect(() => {
    if (!map) return
    const data = new google.maps.Data()
    data.setMap(map)
    dataRef.current = data

    data.setStyle((feature) => {
      const colorKey = (feature.getProperty('colorKey') as string) || 'gray'
      const hex = COLOR_HEX[colorKey] ?? '#857b71'
      return {
        strokeColor: hex,
        strokeWeight: 2,
        fillColor: hex,
        fillOpacity: 0.25,
        // SIGDA puntos: círculo coloreado equivalente al circleMarker de Leaflet.
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          scale: 6,
          strokeColor: hex,
          strokeWeight: 1,
          fillColor: hex,
          fillOpacity: 0.7,
        },
      }
    })

    const clickL = data.addListener('click', (e: google.maps.Data.MouseEvent) => {
      const id = e.feature.getId()
      onSelectRef.current(String(id ?? ''))
    })
    const overL = data.addListener('mouseover', (e: google.maps.Data.MouseEvent) => {
      const name = e.feature.getProperty('name') as string
      const layer = e.feature.getProperty('layer') as string
      if (e.latLng) onHoverRef.current({ position: e.latLng.toJSON(), text: `${name} — ${layer}` })
    })
    const outL = data.addListener('mouseout', () => onHoverRef.current(null))

    return () => {
      clickL.remove()
      overL.remove()
      outL.remove()
      data.setMap(null)
      dataRef.current = null
    }
  }, [map])

  // Re-puebla la capa cuando cambian las features.
  useEffect(() => {
    const data = dataRef.current
    if (!data) return
    data.forEach((f) => data.remove(f))
    onHoverRef.current(null)
    for (const feat of features) {
      const props = feat.properties
      const colorKey = props ? intelRestrictionClassColor(props.restrictionClass) : 'gray'
      data.addGeoJson({
        type: 'Feature',
        id: feat.id,
        geometry: feat.geometry,
        properties: { name: props?.name ?? '', layer: props?.layer ?? '', colorKey },
      })
    }
  }, [features])

  return null
}

// Reporta el bbox del viewport al padre. Debounce 300ms para no disparar
// una petición por frame al hacer pan.
function BboxReporter({ onChange }: { onChange: (b: BboxState) => void }) {
  const map = useMap()
  const timerRef = useRef<number | null>(null)
  const onChangeRef = useRef(onChange)
  useEffect(() => { onChangeRef.current = onChange }, [onChange])

  useEffect(() => {
    if (!map) return
    const fire = () => {
      const b = map.getBounds()
      const z = map.getZoom()
      if (!b || z == null) return
      const ne = b.getNorthEast()
      const sw = b.getSouthWest()
      onChangeRef.current({
        minLng: sw.lng(),
        minLat: sw.lat(),
        maxLng: ne.lng(),
        maxLat: ne.lat(),
        zoom: z,
      })
    }
    const idle = google.maps.event.addListenerOnce(map, 'idle', fire)
    const moveL = map.addListener('idle', () => {
      if (timerRef.current) window.clearTimeout(timerRef.current)
      timerRef.current = window.setTimeout(fire, 300)
    })
    return () => {
      idle.remove()
      moveL.remove()
      if (timerRef.current) window.clearTimeout(timerRef.current)
    }
  }, [map])

  return null
}

export default function ZonasRestringidasPage() {
  const [features, setFeatures] = useState<RestrictedZoneFeature[]>([])
  const [truncated, setTruncated] = useState(false)
  const [loading, setLoading] = useState(false)
  const [bbox, setBbox] = useState<BboxState | null>(null)
  const [sourceFilter, setSourceFilter] = useState<'Ingemmet' | 'Sernanp' | 'CulturaSigda' | 'all'>('all')
  const [layerFilter, setLayerFilter] = useState<string | null>(null)
  const [syncStatus, setSyncStatus] = useState<RestrictedZoneSyncStatus | null>(null)
  const [selectedZoneId, setSelectedZoneId] = useState<string | null>(null)
  const [showFilters, setShowFilters] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [hover, setHover] = useState<HoverState>(null)

  // Sync status (sidebar) — load once and on manual refresh.
  const fetchSyncStatus = useCallback(async () => {
    try {
      const data = await restrictedZonesIntelService.syncStatus()
      setSyncStatus(data)
    } catch (e) {
      // Falla silenciosa: la sidebar es informativa, no bloquea el mapa.
      console.warn('[ZonasRestringidas] syncStatus failed', e)
    }
  }, [])

  useEffect(() => { void fetchSyncStatus() }, [fetchSyncStatus])

  // Bbox-driven feature fetch. Triggered when bbox changes OR filter changes.
  const fetchFeatures = useCallback(async () => {
    if (!bbox) return
    setLoading(true)
    setError(null)
    try {
      const data: RestrictedZoneFeatureCollection = await restrictedZonesIntelService.byBbox({
        minLng: bbox.minLng,
        minLat: bbox.minLat,
        maxLng: bbox.maxLng,
        maxLat: bbox.maxLat,
        source: sourceFilter === 'all' ? undefined : sourceFilter,
        layer: layerFilter ?? undefined,
        limit: 2000,
      })
      setFeatures(data.features)
      setTruncated(data.truncated)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setLoading(false)
    }
  }, [bbox, sourceFilter, layerFilter])

  useEffect(() => { void fetchFeatures() }, [fetchFeatures])

  return (
    <Box position="relative" h="calc(100vh - 120px)" display="flex" flexDirection="column" gap={4}>
      <Flex alignItems="center" justifyContent="space-between" flexShrink={0}>
        <Box>
          <Flex alignItems="center" gap={2}>
            <LuShieldAlert size={22} color="#b23a26" />
            <Text fontSize="2xl" fontWeight="800">Zonas Restringidas</Text>
          </Flex>
          <Text fontSize="sm" color="#857b71">
            Mapa consolidado de restricciones a la actividad minera (INGEMMET · SERNANP · Min. Cultura)
          </Text>
        </Box>
        <Flex gap={2}>
          <Flex
            as="button"
            alignItems="center"
            gap={2}
            px={3}
            py={2}
            borderRadius="md"
            border="1px solid #e8dfd1"
            bg={showFilters ? '#efe8dd' : 'white'}
            fontSize="sm"
            fontWeight="600"
            cursor="pointer"
            onClick={() => setShowFilters((v) => !v)}
          >
            <LuFilter size={14} />
            {showFilters ? 'Ocultar' : 'Filtros'}
          </Flex>
          <Flex
            as="button"
            alignItems="center"
            gap={2}
            px={4}
            py={2}
            borderRadius="md"
            bg="#b4532a"
            color="white"
            fontSize="sm"
            fontWeight="600"
            cursor={loading ? 'wait' : 'pointer'}
            opacity={loading ? 0.7 : 1}
            pointerEvents={loading ? 'none' : 'auto'}
            _hover={{ opacity: loading ? 0.7 : 0.9 }}
            onClick={() => { void fetchSyncStatus(); void fetchFeatures() }}
          >
            <Box as="span" display="inline-flex" style={{ animation: loading ? 'spin 1s linear infinite' : undefined }}>
              <LuRefreshCw size={16} />
            </Box>
            Refrescar
          </Flex>
        </Flex>
      </Flex>

      {error && (
        <Box bg="#fae1db" color="#991b1b" p={3} borderRadius="md" fontSize="sm">{error}</Box>
      )}

      <Flex flex={1} gap={4} minH={0}>
        {showFilters && (
          <Box w="320px" flexShrink={0} overflowY="auto" pr={1}>
            <SyncStatusPanel
              status={syncStatus}
              activeSource={sourceFilter}
              activeLayer={layerFilter}
              onPickSource={(s) => { setSourceFilter(s); setLayerFilter(null) }}
              onPickLayer={(s, l) => { setSourceFilter(s); setLayerFilter(l) }}
              onClearLayer={() => setLayerFilter(null)}
            />
          </Box>
        )}

        <Box flex={1} position="relative" borderRadius="xl" overflow="hidden" border="1px solid #e8dfd1">
          <KMap
            defaultCenter={{ lat: -12, lng: -76 }}
            defaultZoom={6}
            mapTypeId="roadmap"
            mapTypeControl
          >
            <BboxReporter onChange={setBbox} />

            {features.length > 0 && (
              <ZonesDataLayer features={features} onSelect={setSelectedZoneId} onHover={setHover} />
            )}

            {hover && (
              <InfoWindow position={hover.position} pixelOffset={[0, -8]} disableAutoPan headerDisabled>
                <Box fontFamily="'Instrument Sans', sans-serif" px={1} py={0.5}>
                  <Text fontSize="xs" fontWeight="600" color="#1a1a1a">{hover.text}</Text>
                </Box>
              </InfoWindow>
            )}
          </KMap>

          {loading && (
            <Box position="absolute" top={3} left="50%" transform="translateX(-50%)" zIndex={500} bg="white" px={3} py={1.5} borderRadius="full" boxShadow="md" display="flex" alignItems="center" gap={2}>
              <Spinner size="xs" />
              <Text fontSize="xs" fontWeight="600" color="#4f4842">Cargando…</Text>
            </Box>
          )}

          {truncated && !loading && (
            <Box position="absolute" bottom={3} left="50%" transform="translateX(-50%)" zIndex={500} bg="#fff7ed" border="1px solid #fed7aa" color="#9a3412" px={3} py={1.5} borderRadius="md" fontSize="xs" fontWeight="600">
              Mostrando primeras 2000 zonas — haz zoom para ver el resto
            </Box>
          )}

          {!loading && features.length === 0 && (
            <Box position="absolute" top="50%" left="50%" transform="translate(-50%, -50%)" zIndex={500} bg="white" px={4} py={3} borderRadius="md" boxShadow="md" textAlign="center">
              <Text fontSize="sm" color="#857b71">Sin zonas en esta vista</Text>
            </Box>
          )}
        </Box>
      </Flex>

      {selectedZoneId && (
        <ZoneDetailModal
          key={selectedZoneId}
          zoneId={selectedZoneId}
          onClose={() => setSelectedZoneId(null)}
        />
      )}
    </Box>
  )
}
